/**
 * pdfPrint.js — Professional PDF/Print utility for Nexumi reports
 *
 * Opens a styled print window with a professional layout:
 *  - Branded header (accent color + company name + report title)
 *  - Metadata row (date, period, extra info)
 *  - Table with alternating rows and sticky header
 *  - Totals row (bold, accent background)
 *  - Footer with generation timestamp and page numbers
 *
 * Usage:
 *   import { printReport } from '../../utils/pdfPrint';
 *
 *   printReport({
 *     title: 'Lista e Pagave',
 *     subtitle: 'Janar 2026',
 *     company: 'Kompania ABC',
 *     orientation: 'landscape',           // 'portrait' | 'landscape'
 *     columns: [
 *       { label: 'Emri', key: 'name', align: 'left', width: '25%' },
 *       { label: 'Neto (€)', key: 'neto', align: 'right', format: 'currency' },
 *       { label: 'Tatim (€)', key: 'taxes', align: 'right', format: 'currency' },
 *     ],
 *     rows: [...],           // array of objects
 *     totals: { neto: 1200, taxes: 60 },  // optional totals row
 *     badge: { label: '12 punëtorë', color: '#16A34A' },  // optional badge
 *   });
 */

const BRAND_COLOR = '#1D4ED8';  // primary blue
const BRAND_DARK  = '#1e3a5f';  // dark header
const ACCENT      = '#EFF6FF';  // light blue tint
const TOTAL_BG    = '#1D4ED8';  // totals row background
const FONT_STACK  = "'Segoe UI', Arial, sans-serif";

function formatCell(value, format) {
  if (value === null || value === undefined || value === '') return '—';
  if (format === 'currency') {
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);
    return new Intl.NumberFormat('sq-AL', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(n);
  }
  if (format === 'number') {
    const n = Number(value);
    return Number.isFinite(n) ? n.toLocaleString('sq-AL') : String(value);
  }
  if (format === 'date') {
    try { return new Date(value).toLocaleDateString('sq-AL'); } catch { return String(value); }
  }
  return String(value);
}

function getCellValue(col, row) {
  if (typeof col.key === 'function') return col.key(row);
  return row[col.key];
}

function buildTableRows(columns, rows, totals) {
  const rowsHtml = rows.map((row, i) => {
    const cells = columns.map(col => {
      const val = getCellValue(col, row);
      const formatted = formatCell(val, col.format);
      return `<td class="cell" style="text-align:${col.align || 'left'};width:${col.width || 'auto'}">${formatted}</td>`;
    }).join('');
    const rowClass = i % 2 === 1 ? 'row-alt' : 'row-even';
    return `<tr class="${rowClass}">${cells}</tr>`;
  }).join('');

  let totalsHtml = '';
  if (totals) {
    const totalCells = columns.map((col, idx) => {
      if (idx === 0) return `<td class="total-cell total-label">TOTALI</td>`;
      // Support: totals[col.totalKey] or totals[col.key] (when key is string)
      const tKey = col.totalKey || (typeof col.key === 'string' ? col.key : null);
      const val = tKey != null ? totals[tKey] : undefined;
      const formatted = val !== undefined && val !== null ? formatCell(val, col.format) : '';
      return `<td class="total-cell" style="text-align:${col.align || 'left'}">${formatted}</td>`;
    }).join('');
    totalsHtml = `<tr class="totals-row">${totalCells}</tr>`;
  }

  return rowsHtml + totalsHtml;
}

export function printReport({
  title = 'Raport',
  subtitle = '',
  company = '',
  period = '',
  orientation = 'landscape',
  columns = [],
  rows = [],
  totals = null,
  badge = null,
  extraInfo = [],
  filename = 'report',
}) {
  const now = new Date();
  const dateStr = now.toLocaleDateString('sq-AL', { day: '2-digit', month: 'long', year: 'numeric' });
  const timeStr = now.toLocaleTimeString('sq-AL', { hour: '2-digit', minute: '2-digit' });

  const headerCols = columns.map(col =>
    `<th style="text-align:${col.align || 'left'};width:${col.width || 'auto'};white-space:nowrap">${col.label}</th>`
  ).join('');

  const tableRows = buildTableRows(columns, rows, totals);

  const metaItems = [
    period && `<span class="meta-item">📅 ${period}</span>`,
    `<span class="meta-item">👥 ${rows.length} rekorde</span>`,
    ...extraInfo.map(i => `<span class="meta-item">${i}</span>`),
    badge ? `<span class="badge" style="background:${badge.color || BRAND_COLOR}">${badge.label}</span>` : '',
  ].filter(Boolean).join('');

  const html = `<!DOCTYPE html>
<html lang="sq">
<head>
  <meta charset="UTF-8" />
  <title>${title}</title>
  <style>
    @page {
      size: ${orientation === 'landscape' ? 'A4 landscape' : 'A4 portrait'};
      margin: 14mm 12mm 18mm 12mm;
      @bottom-center {
        content: "Faqja " counter(page) " nga " counter(pages);
        font-size: 8pt;
        color: #888;
      }
    }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: ${FONT_STACK};
      font-size: 9pt;
      color: #1a1a2e;
      background: #fff;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    /* ── HEADER ─────────────────────────────────────────────── */
    .report-header {
      background: linear-gradient(135deg, ${BRAND_DARK} 0%, ${BRAND_COLOR} 100%);
      color: white;
      padding: 14px 20px 12px;
      border-radius: 6px 6px 0 0;
      margin-bottom: 0;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
    }
    .header-left {}
    .company-name {
      font-size: 8pt;
      font-weight: 600;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      opacity: 0.85;
      margin-bottom: 4px;
    }
    .report-title {
      font-size: 16pt;
      font-weight: 700;
      letter-spacing: -0.3px;
      line-height: 1.2;
    }
    .report-subtitle {
      font-size: 10pt;
      opacity: 0.8;
      margin-top: 3px;
      font-weight: 400;
    }
    .header-right {
      text-align: right;
      font-size: 7.5pt;
      opacity: 0.75;
      line-height: 1.6;
    }
    .header-logo {
      font-size: 22pt;
      font-weight: 800;
      letter-spacing: -1px;
      opacity: 0.25;
    }

    /* ── META BAR ───────────────────────────────────────────── */
    .meta-bar {
      background: ${ACCENT};
      border: 1px solid #BFDBFE;
      border-top: none;
      padding: 7px 16px;
      display: flex;
      align-items: center;
      gap: 14px;
      flex-wrap: wrap;
      margin-bottom: 12px;
      border-radius: 0 0 4px 4px;
    }
    .meta-item {
      font-size: 8pt;
      color: #1e40af;
      font-weight: 600;
    }
    .badge {
      font-size: 7.5pt;
      color: white;
      padding: 2px 10px;
      border-radius: 20px;
      font-weight: 700;
    }

    /* ── TABLE ──────────────────────────────────────────────── */
    .report-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 8.5pt;
      margin-bottom: 10px;
    }
    .report-table thead tr {
      background: ${BRAND_COLOR};
      color: white;
    }
    .report-table thead th {
      padding: 8px 8px;
      font-weight: 700;
      font-size: 8pt;
      letter-spacing: 0.3px;
      border-right: 1px solid rgba(255,255,255,0.15);
    }
    .report-table thead th:last-child { border-right: none; }
    .row-even { background: #ffffff; }
    .row-alt  { background: #F8FAFF; }
    .report-table tr:hover { background: #EFF6FF; }
    .cell {
      padding: 6px 8px;
      border-bottom: 1px solid #e8edf5;
      vertical-align: middle;
      line-height: 1.3;
    }
    .report-table tr:last-child .cell { border-bottom: none; }

    /* Totals row */
    .totals-row { background: ${TOTAL_BG}; color: white; }
    .total-cell {
      padding: 8px 8px;
      font-weight: 700;
      font-size: 8.5pt;
    }
    .total-label { text-align: left; }

    /* ── FOOTER ─────────────────────────────────────────────── */
    .report-footer {
      border-top: 2px solid ${BRAND_COLOR};
      padding-top: 8px;
      margin-top: 6px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 7.5pt;
      color: #888;
    }
    .footer-brand {
      font-weight: 700;
      color: ${BRAND_COLOR};
      letter-spacing: 0.5px;
    }
    .footer-sign {
      text-align: right;
      line-height: 1.6;
    }
    .signature-line {
      margin-top: 28px;
      border-top: 1px solid #aaa;
      width: 160px;
      display: inline-block;
    }

    /* ── PRINT CONTROLS ─────────────────────────────────────── */
    .no-print { display: block; }
    @media print {
      .no-print { display: none !important; }
      body { font-size: 8pt; }
    }

    /* Print button bar */
    .print-bar {
      background: #f1f5f9;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 12px 16px;
      margin-bottom: 18px;
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .btn-print {
      background: ${BRAND_COLOR};
      color: white;
      border: none;
      border-radius: 6px;
      padding: 9px 22px;
      font-size: 10pt;
      font-weight: 700;
      cursor: pointer;
      font-family: ${FONT_STACK};
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .btn-print:hover { background: ${BRAND_DARK}; }
    .btn-close {
      background: white;
      color: #555;
      border: 1px solid #d1d5db;
      border-radius: 6px;
      padding: 9px 18px;
      font-size: 10pt;
      cursor: pointer;
      font-family: ${FONT_STACK};
    }
    .print-tip { font-size: 8.5pt; color: #64748b; margin-left: 4px; }
  </style>
</head>
<body>

  <!-- Print button bar (hidden when printing) -->
  <div class="no-print print-bar">
    <button class="btn-print" onclick="window.print()">🖨️ &nbsp;Printo / Ruaj si PDF</button>
    <button class="btn-close" onclick="window.close()">✕ &nbsp;Mbyll</button>
    <span class="print-tip">Në shfletues: File → Print → Destination: Save as PDF</span>
  </div>

  <!-- Branded header -->
  <div class="report-header">
    <div class="header-left">
      ${company ? `<div class="company-name">${company}</div>` : ''}
      <div class="report-title">${title}</div>
      ${subtitle ? `<div class="report-subtitle">${subtitle}</div>` : ''}
    </div>
    <div class="header-right">
      <div class="header-logo">HR</div>
      <div>Gjeneruar: ${dateStr}</div>
      <div>Ora: ${timeStr}</div>
    </div>
  </div>

  <!-- Meta bar -->
  <div class="meta-bar">
    ${metaItems || `<span class="meta-item">📊 Raport i gjeneruar nga Nexuma</span>`}
  </div>

  <!-- Data table -->
  <table class="report-table">
    <thead>
      <tr>${headerCols}</tr>
    </thead>
    <tbody>
      ${tableRows || '<tr><td colspan="999" style="text-align:center;padding:20px;color:#888;">Nuk ka të dhëna</td></tr>'}
    </tbody>
  </table>

  <!-- Footer -->
  <div class="report-footer">
    <div>
      <span class="footer-brand">Nexuma</span>
      &nbsp;·&nbsp; Raport konfidencial — vetëm për përdorim të brendshëm
    </div>
    <div class="footer-sign">
      <div>Nënshkrimi i përgjegjësit: ___________________________</div>
      <div>Data: _____________________ &nbsp;&nbsp; Vula: □</div>
    </div>
  </div>

</body>
</html>`;

  const win = window.open('', '_blank', 'width=1100,height=800,scrollbars=yes');
  if (win) {
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    return;
  }

  // Bllokuesi i dritareve kërcyese e ndalte raportin fare, dhe përdoruesi shihte
  // vetëm një alert që shpesh nuk vihej re: butoni «dukej se nuk punon». Raporti
  // nuk ka nevojë për dritare — po ashtu shtypet nga një kornizë e fshehur brenda
  // faqes. Kjo rrugë ndizet VETËM kur dritarja bllokohet, ndaj sjellja e
  // zakonshme e të gjitha raporteve mbetet e pandryshuar.
  printViaFrame(html);
}

/**
 * Shtypje pa dritare kërcyese.
 *
 * Korniza hiqet pas printimit, por jo menjëherë: te disa shfletues dialogu i
 * printimit lexon ende nga dokumenti kur ai mbyllet, dhe heqja e parakohshme e
 * lë faqen bosh.
 */
function printViaFrame(html) {
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
  document.body.appendChild(frame);

  const done = () => { try { document.body.removeChild(frame); } catch { /* u hoq tashmë */ } };

  frame.onload = () => {
    try {
      const w = frame.contentWindow;
      // Brenda kornizës nuk ka ku të klikohet «Printo», ndaj dialogu hapet vetë.
      w.focus();
      w.print();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[pdfPrint] shtypja nga korniza dështoi:', e.message);
    }
    setTimeout(done, 1000);
  };

  const doc = frame.contentWindow?.document;
  if (!doc) { done(); return; }
  doc.open();
  doc.write(html);
  doc.close();
}

/**
 * Convenience: exportPdf with column definitions that use numeric formatting
 */
export function currencyCol(label, key, width) {
  return { label, key, align: 'right', format: 'currency', width: width || 'auto' };
}

export function textCol(label, key, width) {
  return { label, key, align: 'left', format: 'text', width: width || 'auto' };
}

export function numCol(label, key, width) {
  return { label, key, align: 'right', format: 'number', width: width || 'auto' };
}

export function dateCol(label, key, width) {
  return { label, key, align: 'left', format: 'date', width: width || 'auto' };
}
